import { createContext, useState, ReactNode, useContext, JSX } from 'react';
import { Toast, ToastProps } from 'vopay';

type ToastMethod = (message: ReactNode, options?: ToastOptions) => void;

export type ToastContextType = {
  success: ToastMethod;
  neutral: ToastMethod;
  close: () => void;
};

export const ToastContext = createContext<ToastContextType>({
  success: () => {},
  neutral: () => {},
  close: () => {},
});

type ToastOptions = Omit<ToastProps, 'label' | 'isVisible'>;

export const ToastContextProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const [toastProps, setToastProps] = useState<ToastProps>({
    isVisible: false,
    label: '',
  });

  const success = (message: ReactNode, options: ToastOptions = {}) => {
    setToastProps({
      isVisible: true,
      label: message,
      hasCloseButton: false,
      ...options,
    });
  };

  const neutral = (message: ReactNode, options: ToastOptions = {}) => {
    setToastProps({
      isVisible: true,
      label: message,
      variant: 'neutral',
      ...options,
    });
  };

  const close = () => {
    setToastProps((prev) => ({
      ...prev,
      isVisible: false,
    }));
  };

  return (
    <ToastContext.Provider value={{ success, neutral, close }}>
      {children}
      <Toast {...toastProps} onClose={close} />
    </ToastContext.Provider>
  );
};
export const useToast = (): ToastContextType => useContext(ToastContext);
